import { useCallback, useState, useMemo } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { UploadSimple, X, LockKey, CheckCircle, Clock, Copy, QrCode, Trash, ArrowRight, DownloadSimple, Users, Plus, SignIn } from "@phosphor-icons/react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import api from "@/lib/api";
import { downloadFilesAsZip } from "@/lib/zip";
import { trackEvent } from "@/lib/analytics";
import FAQSection from "@/components/FAQSection";
import PageMeta from "@/components/PageMeta";

const fmtBytes = (b) => {
  if (!b && b !== 0) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0;
  while (b >= 1024 && i < u.length - 1) { b /= 1024; i++; }
  return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const EXPIRY_OPTIONS = [
  { v: 1, l: "1h" },
  { v: 6, l: "6h" },
  { v: 24, l: "24h" },
  { v: 72, l: "3d" },
  { v: 168, l: "7d" },
];

export default function Home() {
  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10 md:py-14 fade-up">
      <PageMeta
        title="Send a file · Get a 6-digit code"
        description="SendMe is a friction-free file transfer service. Drop any file, share a 6-digit code, done. Encrypted with TLS 1.3, auto-expires, optional password. No signup required."
        keywords="file transfer, send files, 6-digit code, encrypted file sharing, expiring links, no signup file share, alternative to WeTransfer, alternative to Dropbox"
      />
      {/* Hero */}
      <header className="mb-8 md:mb-12">
        <div className="small-caps mb-3">SendMe · file transfer</div>
        <h1 className="text-3xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
          Send a file. <span className="text-[#FF3366]">Get a 6-digit code.</span>
        </h1>
        <p className="mt-3 text-black/65 max-w-2xl">
          Drag, drop, share. Or paste a code to receive. No accounts required — encrypted, expiring, fast.
        </p>
      </header>

      {/* Send + Receive side by side */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SendPanel />
        <ReceivePanel />
      </div>

      {/* Team workspace CTAs */}
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <Link to="/teams/create" data-testid="home-create-team" className="brut-card p-6 brut-hover-accent flex items-start justify-between gap-4 group">
          <div>
            <div className="small-caps mb-2 flex items-center gap-2"><Plus size={12}/> Team workspace</div>
            <div className="text-xl font-bold tracking-tight mb-1">Create a team</div>
            <p className="text-sm text-black/65">Spin up a shared 1 GB workspace. Add up to 10 members. Auto-generated Team ID.</p>
          </div>
          <Users size={32} weight="duotone" className="text-[#FF3366] shrink-0"/>
        </Link>
        <Link to="/teams/join" data-testid="home-join-team" className="brut-card p-6 brut-hover-secondary flex items-start justify-between gap-4 group">
          <div>
            <div className="small-caps mb-2 flex items-center gap-2"><SignIn size={12}/> Got an invite?</div>
            <div className="text-xl font-bold tracking-tight mb-1">Join a team</div>
            <p className="text-sm text-black/65">Use the 8-character Team ID and password your leader shared with you.</p>
          </div>
          <Users size={32} weight="duotone" className="text-[#10B981] shrink-0"/>
        </Link>
      </div>

      {/* Trust strip */}
      <ul className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
        {[
          ["Encrypted in transit", "TLS 1.3"],
          ["Auto-expires", "1h – 7d"],
          ["Optional password", "bcrypt"],
          ["No sign-up", "for sending"],
        ].map(([t, k]) => (
          <li key={t} className="border border-black/15 p-3">
            <div className="small-caps">{k}</div>
            <div className="mt-1 text-black">{t}</div>
          </li>
        ))}
      </ul>

      {/* FAQ with FAQPage JSON-LD for SEO / AdSense content signal */}
      <FAQSection />

      {/* Resource discovery */}
      <section className="mt-20 md:mt-24 grid md:grid-cols-3 gap-4" data-testid="home-resource-grid">
        <Link to="/help" className="brut-card p-6 brut-hover">
          <div className="small-caps mb-2 text-[#FF3366]">Help center</div>
          <div className="font-bold tracking-tight mb-1">12 guides for everyday flows</div>
          <p className="text-sm text-black/65 leading-relaxed">Step-by-step walkthroughs for sending, receiving, teams, billing, and the trickier corners.</p>
        </Link>
        <Link to="/use-cases" className="brut-card p-6 brut-hover">
          <div className="small-caps mb-2 text-[#FF3366]">Real use cases</div>
          <div className="font-bold tracking-tight mb-1">10 workflows from real users</div>
          <p className="text-sm text-black/65 leading-relaxed">How designers, lawyers, photographers, HR, and doctors actually use SendMe in their day-to-day.</p>
        </Link>
        <Link to="/security" className="brut-card p-6 brut-hover">
          <div className="small-caps mb-2 text-[#FF3366]">Security model</div>
          <div className="font-bold tracking-tight mb-1">The threats we defend against</div>
          <p className="text-sm text-black/65 leading-relaxed">A plain-English explanation of encryption, expiry mechanics, and what's explicitly not in our threat model.</p>
        </Link>
      </section>
    </main>
  );
}

/* --------------------- SEND PANEL --------------------- */
function SendPanel() {
  const [files, setFiles] = useState([]);
  const [password, setPassword] = useState("");
  const [expiry, setExpiry] = useState(24);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const onDrop = useCallback((accepted) => {
    setFiles((prev) => {
      const next = [...prev];
      accepted.forEach((f) => {
        if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f);
      });
      return next;
    });
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: true, maxSize: 100 * 1024 * 1024,
  });

  const totalSize = useMemo(() => files.reduce((a, f) => a + f.size, 0), [files]);
  const removeFile = (idx) => setFiles((f) => f.filter((_, i) => i !== idx));
  const clearAll = () => { setFiles([]); setResult(null); setProgress(0); setPassword(""); };

  const submit = async () => {
    if (!files.length) return toast.error("Add at least one file");
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (password) fd.append("password", password);
    fd.append("expires_in_hours", String(expiry));
    setUploading(true); setProgress(0);
    try {
      const { data } = await api.post("/transfers", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      setResult(data);
      toast.success("Uploaded. Share your code.");
      trackEvent("file_upload", {
        file_count: files.length,
        total_size_bytes: totalSize,
        has_password: Boolean(password),
        expiry_hours: expiry,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const shareUrl = result ? `${window.location.origin}/r/${result.code}` : "";
  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); toast.success(label); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <section className="brut-card p-5 md:p-6" data-testid="send-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-black text-white grid place-items-center text-[10px] font-black">01</div>
          <h2 className="text-xl tracking-tight">Send</h2>
        </div>
        {result && (
          <button onClick={clearAll} data-testid="send-another" className="text-xs uppercase tracking-widest text-black/60 hover:text-[#FF3366]">
            ← New transfer
          </button>
        )}
      </div>

      {!result ? (
        <>
          {/* Compact dropzone */}
          <div
            {...getRootProps()}
            data-testid="dropzone"
            className={`grid-bg cursor-pointer border-2 border-dashed rounded-none px-5 py-10 text-center brut-hover transition-colors ${
              isDragActive ? "border-[#FF3366] bg-[#FF3366]/5" : "border-black/25 hover:border-black"
            }`}
          >
            <input {...getInputProps()} data-testid="file-input" />
            <div className="flex flex-col items-center gap-2">
              <div className="w-12 h-12 border border-black grid place-items-center bg-white">
                <UploadSimple size={22} weight="duotone" className="text-[#FF3366]" />
              </div>
              <div className="text-lg font-bold tracking-tight">
                {isDragActive ? "Release to add" : "Drop files here"}
              </div>
              <div className="text-xs text-black/55 font-mono">click to browse · 100MB / file</div>
            </div>
          </div>

          {/* File list */}
          <AnimatePresence>
            {files.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="mt-4 border border-black"
                data-testid="file-list"
              >
                <div className="px-3 py-2 border-b border-black/15 flex items-center justify-between">
                  <div className="small-caps">{files.length} file{files.length > 1 ? "s" : ""} · {fmtBytes(totalSize)}</div>
                  <button data-testid="clear-files" onClick={() => setFiles([])} className="text-[10px] uppercase tracking-widest text-black/60 hover:text-[#FF3366]">Clear</button>
                </div>
                <ul className="divide-y divide-black/10 max-h-40 overflow-auto">
                  {files.map((f, i) => (
                    <li key={i} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{f.name}</div>
                        <div className="text-[11px] text-black/50 font-mono">{fmtBytes(f.size)}</div>
                      </div>
                      <button onClick={() => removeFile(i)} aria-label="Remove" className="text-black/40 hover:text-[#FF3366]">
                        <X size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Compact settings row */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <div className="small-caps mb-2">Expires</div>
              <div className="grid grid-cols-5 gap-1.5">
                {EXPIRY_OPTIONS.map((o) => (
                  <button
                    key={o.v}
                    onClick={() => setExpiry(o.v)}
                    data-testid={`expiry-${o.v}`}
                    className={`px-1 py-2 text-[11px] uppercase tracking-widest border ${
                      expiry === o.v ? "border-black bg-black text-white" : "border-black/20 text-black/70 hover:text-black"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="small-caps mb-2 flex items-center gap-1"><LockKey size={12}/> Password (opt)</div>
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Optional"
                className="input-brut w-full !py-2 text-sm"
                data-testid="password-input"
                type="password"
                autoComplete="new-password"
              />
            </div>
          </div>

          {uploading && (
            <div className="mt-4" data-testid="upload-progress">
              <div className="small-caps mb-1">Uploading · {progress}%</div>
              <div className="h-1 bg-black/10">
                <div className="h-1 bg-[#FF3366]" style={{ width: `${progress}%`, transition: "width .2s" }} />
              </div>
            </div>
          )}

          <button
            onClick={submit}
            disabled={uploading || files.length === 0}
            data-testid="send-btn"
            className="btn-brut-primary w-full mt-5 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? "Uploading…" : "Generate code"} <ArrowRight size={16} weight="bold"/>
          </button>
        </>
      ) : (
        <TransferResult result={result} shareUrl={shareUrl} copy={copy} />
      )}
    </section>
  );
}

function TransferResult({ result, shareUrl, copy }) {
  const [showQR, setShowQR] = useState(true);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      data-testid="transfer-result"
    >
      <div className="bg-black border border-black p-5 md:p-7 text-center" data-testid="code-display">
        <div className="small-caps mb-3 text-white/45">Your 6-digit code</div>
        <div className="font-mono terminal-glow text-[#FF3366] text-5xl md:text-6xl font-bold tracking-[0.18em] leading-none">
          {result.code.split("").map((d, i) => <span key={i}>{d}</span>)}
        </div>
        <div className="small-caps mt-4 text-white/45 blink">_ Waiting for receiver</div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3">
        <button onClick={() => copy(result.code, "Code copied")} data-testid="copy-code" className="btn-brut !py-2 !px-2 text-[11px]">
          <Copy size={14}/> Code
        </button>
        <button onClick={() => copy(shareUrl, "Link copied")} data-testid="copy-link" className="btn-brut !py-2 !px-2 text-[11px]">
          <Copy size={14}/> Link
        </button>
        <button onClick={() => setShowQR((v) => !v)} data-testid="toggle-qr" className="btn-brut !py-2 !px-2 text-[11px]">
          <QrCode size={14}/> QR
        </button>
      </div>

      {showQR && (
        <div className="mt-4 flex justify-center">
          <div className="border border-black p-3 bg-white" data-testid="qr-code">
            <QRCodeSVG value={shareUrl} size={140} level="M" bgColor="#FFFFFF" fgColor="#0A0A0B"/>
          </div>
        </div>
      )}

      <ul className="mt-4 text-xs font-mono space-y-1.5">
        <li className="flex justify-between"><span className="text-black/50">Expires</span><span>{new Date(result.expires_at).toLocaleString()}</span></li>
        <li className="flex justify-between"><span className="text-black/50">Files</span><span>{result.file_count} · {fmtBytes(result.total_size)}</span></li>
        <li className="flex justify-between"><span className="text-black/50">Password</span><span>{result.has_password ? "Yes" : "No"}</span></li>
      </ul>
    </motion.div>
  );
}

/* --------------------- RECEIVE PANEL --------------------- */
function ReceivePanel() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [transfer, setTransfer] = useState(null);
  const [password, setPassword] = useState("");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // download state — per file (single) or combined (all)
  const [dl, setDl] = useState({ id: null, pct: 0, label: "" });

  const lookup = async () => {
    if (code.length !== 6) return toast.error("Enter a 6-digit code");
    setLoading(true);
    try {
      const { data } = await api.get(`/transfers/by-code/${code}`);
      setTransfer(data);
      setNeedsPassword(data.has_password && (!data.files || data.files.length === 0));
      trackEvent("transfer_lookup", {
        file_count: data?.file_count || 0,
        has_password: Boolean(data?.has_password),
      });
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) toast.error("Code not found");
      else if (s === 410) toast.error("Transfer expired");
      else toast.error("Lookup failed");
      setTransfer(null);
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/transfers/by-code/${code}/verify`, { password });
      setTransfer(data); setNeedsPassword(false);
      toast.success("Unlocked");
    } catch {
      toast.error("Wrong password");
    } finally {
      setLoading(false);
    }
  };

  // Internal: download a single file with progress callback. Returns true on success.
  const fetchOne = async (fileId, filename, onProgress) => {
    try {
      const resp = await api.get(`/transfers/by-code/${code}/download/${fileId}`, {
        params: transfer?.has_password ? { password } : {},
        responseType: "blob",
        onDownloadProgress: (e) => {
          if (e.total) onProgress?.(e.loaded, e.total);
          else onProgress?.(e.loaded, 0);
        },
      });
      const blobUrl = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = blobUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return true;
    } catch {
      return false;
    }
  };

  const download = async (f) => {
    if (dl.id) return;
    setDl({ id: f.id, pct: 0, label: f.filename });
    const ok = await fetchOne(f.id, f.filename, (loaded, total) => {
      const pct = total ? Math.round((loaded / total) * 100) : 0;
      setDl((s) => ({ ...s, pct }));
    });
    if (!ok) toast.error("Download failed");
    else trackEvent("file_download", { file_size_bytes: f.size, mode: "single" });
    setDl({ id: null, pct: 0, label: "" });
  };

  const downloadAll = async () => {
    if (dl.id || !transfer?.files?.length) return;
    const files = transfer.files;
    setDl({ id: "all", pct: 0, label: `Preparing ${files.length} files…` });
    const items = files.map((f) => ({
      url: `/transfers/by-code/${code}/download/${f.id}`,
      params: transfer?.has_password ? { password } : undefined,
      filename: f.filename,
      size: f.size,
    }));
    const res = await downloadFilesAsZip(items, `sendme-${code}`, (pct, label) => {
      setDl({ id: "all", pct, label });
    });
    setDl({ id: null, pct: 0, label: "" });
    if (res.ok) {
      toast.success(`Zipped ${files.length} files`);
      trackEvent("file_download", { file_count: files.length, mode: "zip" });
    } else toast.error(`Download failed${res.failedAt ? `: ${res.failedAt}` : ""}`);
  };

  return (
    <section className="brut-card p-5 md:p-6" data-testid="receive-panel">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-6 h-6 bg-[#10B981] text-white grid place-items-center text-[10px] font-black">02</div>
        <h2 className="text-xl tracking-tight">Receive</h2>
      </div>

      {/* Code input */}
      <div className="small-caps mb-2">Enter 6-digit code</div>
      <div className="flex gap-2">
        <input
          data-testid="code-input"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          onKeyDown={(e) => e.key === "Enter" && lookup()}
          placeholder="000000"
          maxLength={6}
          inputMode="numeric"
          className="input-brut w-full font-mono text-2xl tracking-[0.4em] text-center"
        />
        <button
          data-testid="lookup-btn"
          disabled={code.length !== 6 || loading}
          onClick={lookup}
          className="btn-brut-secondary disabled:opacity-40"
        >
          <ArrowRight size={18} />
        </button>
      </div>

      {/* Password gate */}
      {needsPassword && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4" data-testid="locked-state">
          <div className="small-caps mb-2 flex items-center gap-1"><LockKey size={12}/> Password</div>
          <div className="flex gap-2">
            <input
              type="password"
              data-testid="download-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && verify()}
              className="input-brut w-full"
              placeholder="Enter password"
            />
            <button data-testid="verify-btn" onClick={verify} disabled={!password || loading} className="btn-brut-primary disabled:opacity-40">
              Unlock
            </button>
          </div>
        </motion.div>
      )}

      {/* Files panel */}
      <div className="mt-5">
        {!transfer ? (
          <div className="border border-dashed border-black/20 p-6 text-center text-black/50">
            <DownloadSimple size={28} className="mx-auto mb-2 text-[#10B981]" weight="duotone"/>
            <div className="small-caps">Awaiting code</div>
          </div>
        ) : needsPassword ? (
          <div className="border border-black p-6 text-center">
            <LockKey size={28} className="mx-auto mb-2 text-[#FF3366]" weight="duotone"/>
            <div className="text-base font-bold">Password protected</div>
            <div className="text-xs text-black/55 mt-1">Enter the password to reveal files.</div>
          </div>
        ) : (
          <div className="border border-black" data-testid="files-panel">
            <div className="px-3 py-2 border-b border-black/15 flex items-center justify-between gap-2">
              <div className="small-caps">{transfer.file_count} file{transfer.file_count > 1 ? "s" : ""} · {fmtBytes(transfer.total_size)}</div>
              <div className="flex items-center gap-2">
                {transfer.files.length > 1 && (
                  <button
                    onClick={downloadAll}
                    disabled={dl.id !== null}
                    data-testid="download-all"
                    className="btn-brut-primary !py-1 !px-2 text-[10px] disabled:opacity-40"
                  >
                    <DownloadSimple size={12}/> Download all
                  </button>
                )}
                <div className="text-[10px] font-mono text-black/45">Exp {new Date(transfer.expires_at).toLocaleDateString()}</div>
              </div>
            </div>

            {/* Combined progress for download-all */}
            {dl.id === "all" && (
              <div className="px-3 py-2 border-b border-black/10" data-testid="download-all-progress">
                <div className="flex items-center justify-between text-[11px] font-mono mb-1">
                  <span className="truncate text-black/65">{dl.label}</span>
                  <span className="text-[#10B981]">{dl.pct}%</span>
                </div>
                <div className="h-1 bg-black/10">
                  <div className="h-1 bg-[#10B981] transition-all" style={{ width: `${dl.pct}%` }}/>
                </div>
              </div>
            )}

            <ul className="divide-y divide-black/10 max-h-60 overflow-auto">
              {transfer.files.map((f) => {
                const isDl = dl.id === f.id;
                return (
                  <li key={f.id} className="px-3 py-2">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-sm">{f.filename}</div>
                        <div className="text-[11px] text-black/50 font-mono">{fmtBytes(f.size)}</div>
                      </div>
                      <button
                        onClick={() => download(f)}
                        disabled={dl.id !== null}
                        data-testid={`download-${f.id}`}
                        className="btn-brut-secondary !py-1.5 !px-2 text-[11px] disabled:opacity-40"
                      >
                        <DownloadSimple size={14}/> {isDl ? `${dl.pct}%` : "Get"}
                      </button>
                    </div>
                    {isDl && (
                      <div className="h-1 bg-black/10 mt-1.5" data-testid={`progress-${f.id}`}>
                        <div className="h-1 bg-[#10B981] transition-all" style={{ width: `${dl.pct}%` }}/>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="px-3 py-2 border-t border-black/10 text-right">
              <button onClick={() => navigate(`/r/${code}`)} className="text-[10px] uppercase tracking-widest text-black/55 hover:text-[#FF3366]">
                Open full page →
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
